import axios from "axios";
import {
  IListMessage,
  ILoginRequest,
  INotificationConfig,
  IUser,
  IUserMessageRequest,
  IUserNotificationFilter,
  IUserProfile,
  IUserRequest
} from "@/store/types/user-types";
import { ApiException } from "./ApiExceptions";
import {
  IBookmark,
  IBookmarkPaged,
  IBookmarkParam,
  IBookmarkRequest
} from "@/store/types/bookmark-types";

// Login End Point
export const userLogin = (user: ILoginRequest) =>
  axios({
    url: "customer/login",
    data: user,
    method: "POST"
  })
    .then(response => {
      localStorage.setItem("token", response.data.token);
      axios.defaults.headers.common["Authorization"] = response.data.token;
      return {
        kind: "ok",
        data: response.data.token,
        message: response.data.message
      };
    })
    .catch(error => {
      localStorage.removeItem("token");
      return ApiException(error.response);
    });

export const userLoginGuardian = (token: string) =>
  axios({
    url: "customer/guardian-directly-login",
    data: { token },
    method: "POST"
  })
    .then(response => {
      localStorage.setItem("token", response.data.token);
      axios.defaults.headers.common["Authorization"] = response.data.token;
      return {
        kind: "ok",
        data: response.data.token,
        message: response.data.message
      };
    })
    .catch(error => {
      localStorage.removeItem("token");
      return ApiException(error.response);
    });

export const getUserData = (remove = true) =>
  axios({
    url: "customer/user/me",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      remove && localStorage.removeItem("token");
      return ApiException(error.response);
    });

// Logout End Point
export const userLogout = () =>
  axios({
    url: "customer/logout",
    method: "POST"
  })
    .then(() => {
      axios.defaults.headers.common["Authorization"] = "";
      return { kind: "ok" };
    })
    .catch(() => {
      axios.defaults.headers.common["Authorization"] = "";
      return { kind: "error" };
    });

export const createUserDefault = (capacityId: number, remove = true) =>
  axios({
    url: "customer/capacity",
    data: { capacity_id: capacityId },
    method: "POST"
  })
    .then(response => {
      localStorage.setItem("token", response.data);
      axios.defaults.headers.common["Authorization"] = response.data;
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      remove && localStorage.removeItem("token");
      return ApiException(error.response);
    });

// Bookmarks End Point
export const getBookmarks = (name: string) =>
  axios({
    url: "customer/bookmark/list",
    method: "GET",
    params: { name }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getBookmarksUser = (
  { bookmarkName }: IBookmarkParam = {} as IBookmarkParam
) =>
  axios({
    url: "customer/bookmark/user/list",
    method: "GET",
    params: { name: bookmarkName }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Bookmarks End Point
export const getSignatureBookmarks = (bookmarkName: string) =>
  axios({
    url: "customer/bookmark/list/signature-menu",
    method: "GET",
    params: { tab: bookmarkName }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessBookmarks = ({
  capacityId,
  tab,
  capacity,
  with_children
}: IBookmarkRequest) =>
  axios({
    url: `customer/bookmark/list/process-menu/${capacityId}`,
    method: "GET",
    params: { tab, capacity, with_children }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getBookmarksById = (bookmarkId: string) =>
  axios({
    url: `customer/bookmark/${bookmarkId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const saveBookmark = (bookmark: IBookmark) =>
  axios({
    url: "customer/bookmark",
    data: bookmark,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const editBookmark = (bookmark: IBookmark) =>
  axios({
    url: `customer/bookmark/edit/${bookmark.id}`,
    method: "PATCH",
    data: bookmark
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteBookmark = (bookmarkId: string) =>
  axios({
    url: `customer/bookmark/delete/${bookmarkId}`,
    method: "DELETE"
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getAdmBookmarks = ({
  page,
  paginator_count = 10,
  search,
  partId,
  capacityId,
  order_by,
  ordenation,
  used
}: IBookmarkPaged) =>
  axios({
    url: "customer/global-bookmark/list-edit",
    method: "GET",
    params: {
      page,
      paginator_count,
      word: search,
      part: partId,
      capacity: capacityId,
      order_by,
      ordenation,
      used
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getAdmBookmarksById = (bookmarkId: number) =>
  axios({
    url: `customer/global-bookmark/${bookmarkId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const saveAdmBookmark = (bookmark: IBookmark) =>
  axios({
    url: "customer/global-bookmark",
    data: bookmark,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const editAdmBookmark = (bookmark: IBookmark) =>
  axios({
    url: `customer/global-bookmark/edit/${bookmark.id}`,
    method: "PATCH",
    data: bookmark
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getBookmarkUse = (bookmarkId: string) =>
  axios({
    url: `customer/global-bookmark/check-used/${bookmarkId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteBookmarkAdm = (bookmarkId: string) =>
  axios({
    url: `customer/global-bookmark/${bookmarkId}`,
    method: "DELETE"
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteBookmarkAdmBatch = (bookmarkIdList: string[]) =>
  axios({
    url: `customer/global-bookmark`,
    method: "DELETE",
    data: {
      ids: bookmarkIdList
    }
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Save User Profile(s)
export const setUserProfiles = (
  user: IUser,
  { part_id, capacity_id, profiles, is_secondary }: IUserProfile
) =>
  axios({
    url: `customer/profile`,
    data: {
      part_id,
      capacity_id,
      profiles,
      is_secondary,
      user_name: user.name
    },
    params: { user_id: user.id },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const updateUserProfiles = (
  user: IUser,
  userProfileId: string,
  { part_id, capacity_id, profiles, is_secondary }: IUserProfile
) =>
  axios({
    url: `customer/profile/edit/${userProfileId}`,
    data: {
      part_id,
      capacity_id,
      profiles,
      is_secondary,
      user_name: user.name
    },
    params: { user_id: user.id },
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setProfileActive = (userProfileId: string, active: boolean) =>
  axios({
    url: `customer/profile/activate/${userProfileId}`,
    data: {
      active
    },
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteUserProfiles = (
  userId: number,
  { part_id, capacity_id }: IUserProfile
) =>
  axios({
    url: `customer/profile/delete`,
    data: { part_id, capacity_id },
    params: { user_id: userId },
    method: "DELETE"
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDefaultCapacity = (capacity_id: string) =>
  axios({
    url: `customer/profile/get-default/${capacity_id}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getUserByFilter = (criteria: IUserRequest) =>
  axios({
    url: "customer/search/criteria",
    params: criteria,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getUserByFilterAdm = (criteria: IUserRequest) =>
  axios({
    url: "customer/search/criteria/adm",
    params: criteria,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getUsersByFilterProfiles = (criteria: IUserRequest) =>
  axios({
    url: "customer/search/users/profiles",
    params: criteria,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getUsersByFilterAdm = (criteria: IUserRequest) =>
  axios({
    url: "customer/search/criteria/adm/list",
    params: criteria,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getUsersGenericByFilter = (criteria: IUserRequest) =>
  axios({
    url: "customer/search/user",
    params: criteria,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getNotificationConfig = () =>
  axios({
    url: "customer/notification/config",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getUserNotificationConfig = () =>
  axios({
    url: "customer/notification/config/user",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setUserNotificationConfig = (
  userNotificationConfig: INotificationConfig[]
) =>
  axios({
    url: "customer/notification/config/user/edit",
    method: "PUT",
    data: userNotificationConfig
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getNotificationsList = ({
  currentPage,
  perPage,
  isReaded,
  capacity,
  types,
  date,
  search,
  order_by,
  only_count
}: IUserNotificationFilter) =>
  axios({
    url: "customer/notification/list",
    params: {
      page: currentPage,
      paginator_count: perPage,
      is_readed: isReaded,
      capacity,
      types,
      date,
      search,
      order_by,
      only_count
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setNotificationReaded = (notificationsIds: string[]) =>
  axios({
    url: `customer/notification/readed`,
    method: "PATCH",
    data: { notification_ids: notificationsIds }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setAllNotificationReaded = () =>
  axios({
    url: `customer/notification/readed/all`,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteNotification = (notificationsIds: string[]) =>
  axios({
    url: `customer/notification/delete`,
    method: "DELETE",
    data: { notification_ids: notificationsIds }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Get Digital Cert End Point Token
export const userTokenLoginDigitalCert = () =>
  axios({
    url: "customer/certification/token",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Login Digital Cert End Point
export const userLoginDigitalCert = (hash: string) =>
  axios({
    url: "customer/certification/token/validation",
    data: { hash },
    method: "post"
  })
    .then(response => {
      localStorage.setItem("token", response.data.token);
      axios.defaults.headers.common["Authorization"] = response.data.token;
      return { kind: "ok", data: response.data.token };
    })
    .catch(error => {
      localStorage.removeItem("token");
      return ApiException(error.response);
    });

// Message Area endpoints
export const listMessagesAdm = ({
  page,
  size,
  start_date,
  end_date,
  search
}: IUserMessageRequest) =>
  axios({
    url: "customer/notice/list/admin",
    params: {
      page,
      size,
      start_date,
      end_date,
      search
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const createMessage = ({ title, description }: IListMessage) =>
  axios({
    url: "customer/notice/create",
    method: "POST",
    data: {
      title,
      description
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteMessage = (messagesIds: number[]) =>
  axios({
    url: `customer/notice/delete`,
    method: "DELETE",
    data: { notice_ids: messagesIds }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const listMessages = ({ page, size, only_count }: IUserMessageRequest) =>
  axios({
    url: "customer/notice/list/",
    params: {
      page,
      size,
      only_count
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const readMessage = (messageId: string) =>
  axios({
    url: `customer/notice/${messageId}/read`,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const readAllMessage = () =>
  axios({
    url: "customer/notice/read/all",
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const disableNotificationPublic = (hash: string) =>
  axios({
    url: `/customer/notification/config/disable`,
    params: { hash },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });
